import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { ArrowLeft, Plus, Copy, Check, Users, MessageSquare, Send, Key, Shield, UserPlus, UserMinus, Ban, UserCheck, Lock, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isDarkMode } = useTheme();
  const [inviteCodes, setInviteCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [showAdminManagement, setShowAdminManagement] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [formData, setFormData] = useState({ maxUses: 1, expiresInDays: null, note: '' });
  // User management
  const [activeTab, setActiveTab] = useState('codes'); // 'codes', 'users', 'admins'
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/chats');
      return;
    }
    fetchInviteCodes();
    fetchStats();
    fetchAdmins();
    fetchAllUsers();
  }, []);

  const fetchInviteCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/invite-codes`, { headers: { Authorization: `Bearer ${token}` } });
      setInviteCodes(response.data);
    } catch (error) { console.error('Failed to fetch invite codes:', error); }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(response.data);
    } catch (error) { console.error('Failed to fetch stats:', error); }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/list`, { headers: { Authorization: `Bearer ${token}` } });
      setAdmins(response.data);
    } catch (error) { console.error('Failed to fetch admins:', error); }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(response.data);
    } catch (error) { console.error('Failed to fetch users:', error); }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    try {
      const response = await axios.get(`${API}/users/search`, { params: { query }, headers: { Authorization: `Bearer ${token}` } });
      setSearchResults(response.data);
    } catch (error) { console.error('Search failed:', error); }
  };

  const promoteToAdmin = async (userId) => {
    try {
      await axios.post(`${API}/admin/promote/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User promoted to admin!');
      fetchAdmins(); fetchStats(); fetchAllUsers();
      setSearchQuery(''); setSearchResults([]);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to promote user'); }
  };

  const demoteAdmin = async (userId) => {
    try {
      await axios.post(`${API}/admin/demote/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Admin demoted successfully');
      fetchAdmins(); fetchStats(); fetchAllUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to demote admin'); }
  };

  const createInviteCode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/invite-codes`, {
        max_uses: parseInt(formData.maxUses),
        expires_in_days: formData.expiresInDays ? parseInt(formData.expiresInDays) : null,
        note: formData.note || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invite code created!');
      setShowCreateForm(false);
      setFormData({ maxUses: 1, expiresInDays: null, note: '' });
      fetchInviteCodes(); fetchStats();
    } catch (error) { toast.error('Failed to create invite code'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const deactivateCode = async (codeId) => {
    try {
      await axios.delete(`${API}/admin/invite-codes/${codeId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invite code deactivated');
      fetchInviteCodes(); fetchStats();
    } catch (error) { toast.error('Failed to deactivate code'); }
  };

  const handleUserAction = async (userId, action) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User ${action}d successfully`);
      fetchAllUsers(); fetchStats();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${action} user`); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${resetPasswordModal.id}/reset-password`, { new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Password reset for ${resetPasswordModal.display_name}`);
      setResetPasswordModal(null);
      setNewPassword('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reset password'); }
  };

  const filteredUsers = allUsers.filter(u => {
    if (!userSearchQuery.trim()) return true;
    const q = userSearchQuery.toLowerCase();
    return u.display_name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q);
  });

  const statusColors = {
    active: 'bg-green-500',
    suspended: 'bg-yellow-500',
    deactivated: 'bg-red-500'
  };

  if (!user?.is_admin) return null;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-950' : 'bg-background-DEFAULT'}`} data-testid="admin-panel">
      <div className={`p-6 border-b ${isDarkMode ? 'border-gray-800' : 'border-border-DEFAULT'}`}>
        <div className="chat-container flex items-center gap-4">
          <button data-testid="back-btn" onClick={() => navigate('/chats')} className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-secondary-DEFAULT'}`}>
            <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-text-primary'} />
          </button>
          <div>
            <h1 className={`font-heading text-2xl ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Admin Panel</h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>Manage users, codes and settings</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="chat-container max-w-6xl mx-auto">
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { icon: Users, label: 'Total Users', value: stats.total_users },
                { icon: Shield, label: 'Admins', value: stats.total_admins || 1 },
                { icon: MessageSquare, label: 'Total Chats', value: stats.total_chats },
                { icon: Send, label: 'Messages', value: stats.total_messages },
                { icon: Key, label: 'Active Codes', value: stats.active_invite_codes }
              ].map((stat, i) => (
                <div key={i} className={`rounded-2xl p-5 shadow-sm border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                      <stat.icon size={20} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{stat.value}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>{stat.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'codes', label: 'Invite Codes' },
              { id: 'users', label: 'User Management' },
              { id: 'admins', label: 'Admin Management' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#D4AF37] text-white'
                    : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Invite Codes Tab */}
          {activeTab === 'codes' && (
            <div>
              <div className="mb-6">
                {!showCreateForm ? (
                  <button data-testid="create-invite-btn" onClick={() => setShowCreateForm(true)} className="h-12 px-6 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium shadow-gold hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2">
                    <Plus size={20} /> Create Invite Code
                  </button>
                ) : (
                  <div className={`rounded-3xl p-6 shadow-sm border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'}`}>
                    <h3 className={`font-heading text-xl mb-4 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>Create New Invite Code</h3>
                    <form onSubmit={createInviteCode} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>Maximum Uses</label>
                          <input type="number" value={formData.maxUses} onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })} className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} min="1" required />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>Expires In (Days)</label>
                          <input type="number" value={formData.expiresInDays || ''} onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })} className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Never" min="1" />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-text-primary'}`}>Note (Optional)</label>
                        <input type="text" value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} className={`w-full h-12 border-none rounded-xl px-4 focus:ring-1 focus:ring-[#D4AF37] ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="e.g., For friends, Beta testers..." />
                      </div>
                      <div className="flex gap-3">
                        <button type="submit" className="h-12 px-6 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium">Create Code</button>
                        <button type="button" onClick={() => setShowCreateForm(false)} className={`h-12 px-6 rounded-full border ${isDarkMode ? 'border-gray-700 text-gray-300' : 'border-[#E5E5E5] text-[#1A1A1A]'}`}>Cancel</button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              <h2 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>INVITE CODES</h2>
              <div className="space-y-3">
                {inviteCodes.length === 0 ? (
                  <div className={`text-center py-12 rounded-3xl border ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-400' : 'bg-white border-border-DEFAULT text-text-secondary'}`}>
                    <p>No invite codes yet. Create one to get started!</p>
                  </div>
                ) : (
                  inviteCodes.map((code) => (
                    <div key={code.id} className={`rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`font-mono text-2xl font-bold ${isDarkMode ? 'text-[#D4AF37]' : 'text-primary'}`}>{code.code}</span>
                            <button onClick={() => copyCode(code.code)} className={`h-8 w-8 rounded-full transition-colors flex items-center justify-center ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-secondary-DEFAULT'}`}>
                              {copiedCode === code.code ? <Check size={16} className="text-green-500" /> : <Copy size={16} className={isDarkMode ? 'text-gray-400' : 'text-text-secondary'} />}
                            </button>
                            {!code.is_active && <span className="px-3 py-1 bg-red-500/10 text-red-500 text-xs rounded-full">Inactive</span>}
                          </div>
                          <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                            <div><span className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}>Uses:</span> <span className="ml-1 font-medium">{code.used_count} / {code.max_uses}</span></div>
                            <div><span className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}>Created:</span> <span className="ml-1">{format(new Date(code.created_at), 'MMM dd, yyyy')}</span></div>
                            {code.expires_at && <div><span className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}>Expires:</span> <span className="ml-1">{format(new Date(code.expires_at), 'MMM dd, yyyy')}</span></div>}
                            {code.note && <div><span className={isDarkMode ? 'text-gray-500' : 'text-text-tertiary'}>Note:</span> <span className="ml-1">{code.note}</span></div>}
                          </div>
                        </div>
                        {code.is_active && (
                          <button onClick={() => deactivateCode(code.id)} className="ml-4 h-10 px-4 rounded-full text-red-500 hover:bg-red-50 transition-colors text-sm font-medium">Deactivate</button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="mb-4">
                <div className="relative">
                  <Search size={18} className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className={`w-full h-12 rounded-xl pl-12 pr-4 focus:ring-1 focus:ring-[#D4AF37] ${isDarkMode ? 'bg-gray-800 text-white border-none' : 'bg-[#F9F9F9] text-[#1A1A1A] border-none'}`}
                    placeholder="Search users..."
                    data-testid="user-search-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                {filteredUsers.map(u => (
                  <div key={u.id} className={`flex items-center gap-4 p-4 rounded-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'}`} data-testid={`user-row-${u.id}`}>
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                      {(u.display_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{u.display_name}</span>
                        <div className={`w-2 h-2 rounded-full ${statusColors[u.status || 'active']}`} title={u.status || 'active'} />
                        <span className={`text-xs capitalize ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{u.status || 'active'}</span>
                        {u.is_admin && <span className="px-2 py-0.5 rounded-full text-xs bg-[#D4AF37]/20 text-[#D4AF37]">Admin</span>}
                      </div>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{u.username}</span>
                    </div>
                    {u.id !== user.id && (
                      <div className="flex items-center gap-2">
                        {(u.status || 'active') === 'active' && (
                          <button onClick={() => handleUserAction(u.id, 'suspend')} className={`px-3 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20' : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'}`} data-testid={`suspend-${u.id}`}>
                            <Ban size={12} className="inline mr-1" />Suspend
                          </button>
                        )}
                        {(u.status || 'active') === 'active' && (
                          <button onClick={() => handleUserAction(u.id, 'deactivate')} className={`px-3 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'}`} data-testid={`deactivate-${u.id}`}>
                            Deactivate
                          </button>
                        )}
                        {(u.status === 'suspended' || u.status === 'deactivated') && (
                          <button onClick={() => handleUserAction(u.id, 'activate')} className={`px-3 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20' : 'bg-green-50 text-green-600 hover:bg-green-100'}`} data-testid={`activate-${u.id}`}>
                            <UserCheck size={12} className="inline mr-1" />Activate
                          </button>
                        )}
                        <button onClick={() => setResetPasswordModal(u)} className={`px-3 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`reset-pwd-${u.id}`}>
                          <Lock size={12} className="inline mr-1" />Reset Pwd
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Management Tab */}
          {activeTab === 'admins' && (
            <div className="space-y-6">
              <div className={`rounded-3xl p-6 shadow-sm border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'}`}>
                <h3 className={`font-heading text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                  <Shield size={20} className="text-[#D4AF37]" /> Current Admins ({admins.length})
                </h3>
                <div className="space-y-3">
                  {admins.map((admin, index) => (
                    <div key={admin.id} className={`flex items-center justify-between p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-secondary-DEFAULT'}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">
                          {admin.display_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{admin.display_name}</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{admin.username}</p>
                        </div>
                        {index === 0 && <span className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full font-medium">Super Admin</span>}
                      </div>
                      {index !== 0 && (
                        <button onClick={() => { if (window.confirm(`Remove admin privileges from ${admin.display_name}?`)) demoteAdmin(admin.id); }} className="h-10 px-4 rounded-full text-red-500 hover:bg-red-50 text-sm font-medium flex items-center gap-2">
                          <UserMinus size={16} /> Demote
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className={`rounded-3xl p-6 shadow-sm border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-border-DEFAULT'}`}>
                <h3 className={`font-heading text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>
                  <UserPlus size={20} className="text-[#D4AF37]" /> Promote User to Admin
                </h3>
                <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }} className={`w-full h-12 border-none rounded-xl px-4 mb-4 focus:ring-1 focus:ring-[#D4AF37] ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`} placeholder="Search users by username or display name..." />
                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map((foundUser) => (
                      <div key={foundUser.id} className={`flex items-center justify-between p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-secondary-DEFAULT'}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium">{foundUser.display_name.charAt(0).toUpperCase()}</div>
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-text-primary'}`}>{foundUser.display_name}</p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-text-secondary'}`}>@{foundUser.username}</p>
                          </div>
                        </div>
                        <button onClick={() => promoteToAdmin(foundUser.id)} className="h-10 px-4 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white hover:scale-105 transition-all text-sm font-medium flex items-center gap-2">
                          <UserPlus size={16} /> Promote
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setResetPasswordModal(null)}>
          <div className={`w-full max-w-sm rounded-2xl p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-heading text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Reset Password</h3>
              <button onClick={() => setResetPasswordModal(null)} className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}><X size={20} /></button>
            </div>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Reset password for <strong>{resetPasswordModal.display_name}</strong> (@{resetPasswordModal.username})
            </p>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`w-full h-12 border-none rounded-xl px-4 mb-4 focus:ring-1 focus:ring-[#D4AF37] ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-[#F9F9F9] text-[#1A1A1A]'}`}
              placeholder="New password (min 6 chars)"
              data-testid="reset-password-input"
            />
            <button onClick={handleResetPassword} className="w-full h-12 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-white font-medium" data-testid="reset-password-submit">
              Reset Password
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
